<template>
    <div class="ingredient-configuration" :class="[$mq, { plk: plk }]">
        <div class="gram-title">
            {{ plk ? $t('production.plk.configuration.title') : $t('production.configuration.title') }}
            <span v-if="plk" class="start-demo" @click="startDemo()"> <!-- VIDEO PLS-STOCK -->
                Start demo
            </span>
        </div>
        <div class="content">
            <div class="ingredient">
                <p v-if="plk" class="subtitle">{{ $t('production.plk.configuration.subtitle')}}</p>
                <div class="ingredients" v-for="product in sortedProducts" v-if="product" :key="product.id">
                    <div class="product-info">
                        <button class="toggle-switch" @click="toggleTempProduct(product.id)" :class="{ active: product.status }">
                            <div :class="{ slider: true, active: product.status }"></div>
                        </button>
                        <div class="name">{{ product.name }}</div>
                    </div>
                    <template v-if="product.containers">
                        <select v-model="product.active_container" @change="updateActiveContainer(product.id, product.active_container)" :disabled="Object.keys(product.containers).length <= 1">
                            <option v-for="container in product.containers" :key="container.id" :value="container.id">
                                {{ container.name }}
                            </option>
                        </select>
                    </template>
                    <template v-else-if="product.packages">
                        <select v-model="product.active_package" :disabled="Object.keys(product.packages).length <= 1">
                            <option v-for="container in product.packages" :key="container.id" :value="container.id">
                                {{ container.name }}
                            </option>
                        </select>
                    </template>
                    <template v-else-if="product.drops">
                        <select v-model="product.active_drop" :disabled="Object.keys(product.drops).length <= 1">
                            <option v-for="drop in product.drops" :key="drop.id" :value="drop.id">
                                {{ drop.name }}
                            </option>
                        </select>
                    </template>
                    <template v-else-if="product.units">
                        <select v-model="product.active_unit" :disabled="Object.keys(product.units).length <= 1">
                            <option v-for="unit in product.units" :key="unit.id" :value="unit.id">
                                {{ unit.name }}
                            </option>
                        </select>
                    </template>
                </div>
                <div class="reset-card" :class="{ 'on-cooldown': !resetAvailable }" v-if="plk && canResetPls">
                    <div class="reset-card-header">
                        <p class="reset-card-title">{{ $t('production.plk.reset.title') }}</p>
                        <span class="reset-card-cooldown" v-if="showResetCooldown">
                            <span class="reset-card-cooldown-dot" aria-hidden="true"></span>
                            {{ resetCooldownLabel }}
                        </span>
                    </div>
                    <p class="reset-card-description">{{ $t('production.plk.reset.description') }}</p>
                    <button type="button" class="reset-card-button" :disabled="!resetAvailable" @click="openResetPopup()">
                        <i class="i-andy-refresh-2 i-andy-s" aria-hidden="true"></i>
                        <span>{{ $t('production.plk.reset.button') }}</span>
                    </button>
                </div>
            </div>
            <div class="fryers" v-if="plk && fryers.length > 0">
                <p class="title">{{ $t('production.plk.fryers.title') }}</p>
                <div class="fryer">
                    <select v-model="tempFryers" @change="updateFryers(tempFryers)">
                        <option v-for="i in fryerUnitChoices" :key="i" :value="i" :selected="tempFryers == i">
                            {{ i }}
                        </option>
                    </select>
                </div>
            </div>
        </div>
        <div class="btnExit">
            <button class="btn cancel-changes" @click="cancelChanges()">{{ $t('production.configuration.cancel') }}</button>
            <button class="btn save-changes" @click="saveChanges()">{{ $t('production.configuration.saveChanges') }}</button>
        </div>
    </div>
</template>

<script>
const RESET_MIN_ROLE_LEVEL = 3
/** Cada cuánto se refresca el contador de espera del reseteo. */
const COOLDOWN_REFRESH_MS = 30000
const MS_PER_MINUTE = 60000
const MINUTES_PER_HOUR = 60

export default {
    name: 'Configuration',
    props: {
        types: { type: Array },
        plk: { type: Boolean, default: false },
        fryers: { type: Array, default: () => [] },
        date: { type: String, default: '' }
    },
    data() {
        return {
            tempProducts: {},
            tempFryers: [],
            now: Date.now(),
            cooldownInterval: null,
            refreshedAvailableAt: null
        }
    },
    computed: {
        canResetPls() {
            const roleLevel = this.$store.getters['loginUser/getUserRoleLevel']

            return roleLevel !== null && roleLevel >= RESET_MIN_ROLE_LEVEL
        },
        /** Minutos que faltan para poder volver a resetear, 0 si ya está disponible. */
        resetCooldownMinutes() {
            const resetStatus = this.$store.getters['production/getPlsResetStatus']
            if (!resetStatus || !resetStatus.available_at) {
                return 0
            }

            const remaining = new Date(resetStatus.available_at).getTime() - this.now

            return remaining > 0 ? Math.ceil(remaining / MS_PER_MINUTE) : 0
        },
        resetAvailable() {
            const resetStatus = this.$store.getters['production/getPlsResetStatus']

            // Sin respuesta del servidor no se puede saber si toca esperar: mantenerlo bloqueado.
            if (!resetStatus || !resetStatus.loaded) {
                return false
            }

            // Si hay fecha de disponibilidad, el contador manda: can_reset es una foto del momento de la carga.
            if (resetStatus.available_at) {
                return this.resetCooldownMinutes === 0
            }

            return resetStatus.can_reset !== false
        },
        /** La etiqueta de espera solo tiene sentido si queda tiempo real por delante. */
        showResetCooldown() {
            return !this.resetAvailable && this.resetCooldownMinutes > 0
        },
        resetCooldownLabel() {
            const hours = Math.floor(this.resetCooldownMinutes / MINUTES_PER_HOUR)
            const minutes = this.resetCooldownMinutes % MINUTES_PER_HOUR

            if (hours === 0) {
                return this.$t('production.plk.reset.cooldown_minutes', { minutes: minutes })
            }

            return this.$t('production.plk.reset.cooldown', { hours: hours, minutes: minutes })
        },
        fryerUnitBounds() {
            let equipmentConfig = this.$store.getters['production/getOption']('PLS_DEVICE_CONFIG')
            if(!equipmentConfig) {
                return { min: 2, max: 4 }
            }
            return equipmentConfig.units
        },
        fryerUnitChoices() {
            const { min, max } = this.fryerUnitBounds
            const list = []
            for (let n = min; n <= max; n++) {
                list.push(n)
            }
            return list
        },
        sortedProducts() {
            return Object.values(this.tempProducts).sort((a, b) => a.name.localeCompare(b.name))
        }
    },
    created() {
        this.loadProducts()
        this.formatFryers()

        if (!this.plk) {
            return
        }

        this.$store.dispatch('production/loadPlsResetStatus', { date: this.date })
        this.cooldownInterval = setInterval(() => {
            this.now = Date.now()
            this.refreshResetStatusIfExpired()
        }, COOLDOWN_REFRESH_MS)
    },
    beforeDestroy() {
        clearInterval(this.cooldownInterval)
    },
    methods: {
        /** Cuando vence la espera, vuelve a pedir el estado al servidor una sola vez por vencimiento. */
        refreshResetStatusIfExpired() {
            if (!this.plk) {
                return
            }

            const resetStatus = this.$store.getters['production/getPlsResetStatus']
            if (!resetStatus || !resetStatus.available_at || this.resetCooldownMinutes > 0) {
                return
            }

            if (this.refreshedAvailableAt === resetStatus.available_at) {
                return
            }

            this.refreshedAvailableAt = resetStatus.available_at
            this.$store.dispatch('production/loadPlsResetStatus', { date: this.date })
        },
        openResetPopup() {
            if (!this.resetAvailable) {
                return
            }

            const stock = this.$store.getters['production/getPlkStock']
            const productsList = this.sortedProducts
                .filter((product) => product.status)
                .map((product) => {
                    const stockItem = stock.find((item) => item.id == product.id)

                    return {
                        id: product.id,
                        name: product.name,
                        quantity: stockItem ? stockItem.units : 0
                    }
                })

            this.$popup.plsReset({
                data: {
                    productsList,
                    businessDate: this.date
                },
                onSuccess: () => {
                    this.$emit('updateForecast')
                }
            })
        },
        loadProducts() {
            let allProducts = JSON.parse(JSON.stringify(this.$store.getters['production/getProducts']))
            this.tempProducts = {}

            // filter only products that have param type in this.types
            if (this.types) {
                for (var item in allProducts) {
                    if (this.types.includes(allProducts[item].type)) {
                        this.tempProducts[allProducts[item].id] = allProducts[item]
                    }
                }
            } else {
                this.tempProducts = allProducts
            }
        },
        formatFryers(){
            const { min, max } = this.fryerUnitBounds
            const len = this.fryers.length || min
            this.tempFryers = Math.min(Math.max(len, min), max)
        },
        updateFryers(fryers){
            this.tempFryers = fryers
        },
        cancelChanges() {
            this.loadProducts()
            this.$store.commit('production/setConfiguration', false)
        },
        saveChanges() {
            var products = []
            for (var item in this.tempProducts) {
                const p = this.tempProducts[item]
                const product = {
                    id: p.id,
                    status: p.status ? 1 : 0,
                }

                if (p.containers) {
                    product.container = p.active_container
                }
                if (p.packages) {
                    product.package = p.active_package
                }
                if (p.drops) {
                    product.drop = p.active_drop
                }
                if (p.units) {
                    product.unit = p.active_unit
                }

                products.push(product)
            }
            var params = {
                products: JSON.stringify(products)
            }
            // console.warn(params)

            var self = this

            this.$overlay.loading()
            this.$store.dispatch('production/updateConfiguration', params).then(() => {
                self.$overlay.hide()
                //self.$store.commit('production/setProducts', self.tempProducts)

                self.$store.dispatch('production/loadData').then(() => {
                })
            })

            if(this.plk){
                const finalFryers = [];
                for(let i = 1; i <= this.tempFryers; i++){
                    finalFryers.push({ id: i , status: 1 })
                }

                this.$store.commit('production/setFryers', finalFryers)
                this.$store.dispatch('production/toggleFryer', finalFryers)
            }
            this.$store.commit('production/setConfiguration', false)
        },
        toggleTempProduct(productId) {
            if (this.tempProducts[productId]) {
                this.tempProducts[productId].status = !this.tempProducts[productId].status
                this.tempProducts = JSON.parse(JSON.stringify(this.tempProducts))
            }
        },
        updateActiveContainer(productId, activeContainerId) {
            if (this.tempProducts[productId]) {
                this.tempProducts[productId].active_container = activeContainerId
            }
        },
        startDemo() {
            let self = this
            this.$overlay.loading()
            this.$store.dispatch('production/startDemo').then(() => {
                let date = moment().format('YYYY-MM-DD')
                // self.$store.dispatch('production/loadForecast', { date: date, type: 'stock-pls' }).then(() => {
                    self.$overlay.hide()
                    self.$snackbar.success({message:"Se han cargado los productos de demo"})
                    self.$emit('updateForecast')
                // })
            })
        }
    }
}
</script>
<style lang="scss">
.ingredient-configuration {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 16px;
    padding-bottom: 0;

    .subtitle {
        font-family: $text-bold;
        @include font-size(16px);
        padding: 0;
    }

    .content {
        display: flex;
        flex-direction: row;
        flex-grow: 1;
    }

    // PLS CHEF: la lista de gramajes no ocupa todo el ancho, deja sitio a la columna de equipos
    &.plk .ingredient {
        width: 38%;
    }

    .reset-card {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0;
        flex-shrink: 0;
        width: 100%;
        margin-top: 24px;
        padding: 16px 16px 16px 16px;
        box-sizing: border-box;
        border: 1px solid $color-neutral-300;
        border-radius: 8px;
        background-color: $color-white;
        @include default-box-shadow($b: 4px, $o: 0.15);

        .reset-card-header {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
            width: 100%;
            margin-bottom: 12px;
        }

        .reset-card-title {
            font-family: 'Avenir Next LT Pro', $text-bold, sans-serif;
            font-weight: 700;
            font-size: 20px;
            line-height: 1.2;
            margin: 0;
            padding: 0;
            color: $color-neutral-800;
        }

        .reset-card-cooldown {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 8px;
            flex-shrink: 0;
            padding: 6px 12px;
            border-radius: 16px;
            background-color: $color-neutral-200;
            font-family: 'Avenir Next LT Pro', $text, sans-serif;
            font-size: 14px;
            line-height: 1.2;
            color: $color-neutral-600;

            .reset-card-cooldown-dot {
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background-color: $color-neutral-500;
            }
        }

        .reset-card-description {
            font-family: 'Avenir Next LT Pro', $text, sans-serif;
            font-weight: 400;
            font-size: 14px;
            line-height: 1.4;
            margin: 0 0 18px 0;
            padding: 0;
            color: #7a7a7a;
        }

        .reset-card-button {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 6px;
            margin-top: 0;
            padding: 8px;
            border: none;
            border-radius: 4px;
            font-family: 'Avenir Next LT Pro', $text-bold, sans-serif;
            font-weight: 700;
            font-size: 16px;
            line-height: 1;
            color: $color-white;
            background-color: $color-error-600;
            cursor: pointer;

            i {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 16px;
                height: 16px;
                font-size: 16px;
                line-height: 16px;
                color: $color-white;
            }

            span {
                color: $color-white;
                margin-top: 2px;
            }
        }

        // Estado de espera: aún no ha pasado el tiempo mínimo desde el último reseteo.
        &.on-cooldown {
            .reset-card-title {
                color: $color-neutral-600;
            }

            .reset-card-description {
                color: $color-neutral-500;
            }

            .reset-card-button {
                background-color: $color-neutral-100;
                cursor: not-allowed;

                i,
                span {
                    color: $color-neutral-600;
                }
            }
        }
    }

    /* PLS SIZES MONITORS*/
    &.landscape {
        padding: 0px;
        .gram-title {
            padding: 16px;
            margin-bottom: 0px;
        }

        .content {
            padding: 16px;

            .ingredient {
                height: 74vh;
            }
        }
    }
}
.gram-title {
    font-family: $text-bold;
    @include font-size(l);
    margin-bottom: 16px;
    .start-demo {
        margin-left: 16px;
        font-family: $text;
        @include font-size(14px);
        cursor: pointer;
        background-color: $color-primary-700;
        color: $color-white;
        padding: 4px 8px;
        border-radius: 4px;
    }
}
.ingredient {
    height: 80vh;
    overflow-y: auto;
    width: 100%;
    padding: 16px;
    display: flex;
    flex-direction: column;

    .ingredients {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        position: relative;
        padding: 4px 0;

        .product-info {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: flex-start;
        }

        .toggle-switch {
            width: 40px;
            height: 24px;
            background-color: $color-selector2;
            border-radius: 18px;
            position: relative;
            transition: background-color 0.3s;
            border: none;
            padding: 0;
            margin-right: 5px;

            &.active {
                background-color: $color-primary-700;
            }

            .slider {
                width: 18px;
                height: 18px;
                background-color: $color-selector;
                border-radius: 50%;
                position: absolute;
                top: 3px;
                left: 4px;
                transition: left 0.3s;

                &.active {
                    left: 18px;
                }
            }
        }

        .name {
            font-family: $text;
            padding-left: 4px;
        }

        select {
            left: 50%;
            width: 250px;
            font-family: $text;
            border: 1px solid #ccc;
            border-radius: 3px;
            padding: 8px 16px;
            color: $color-black;
        }
    }
}

.fryers {
    display: flex;
    flex-direction: column;
    padding: 16px;
    gap:8px;
    width: 35%;

    .fryer {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-start;
        position: relative;
        gap:8px;

        .title {
            padding: 0;
        }

        select {
            width: 100px;
            font-family: $text;
            border: 1px solid #ccc;
            border-radius: 3px;
            padding: 8px 16px;
            color: $color-black;
        }

        .name {
            font-family: $text;
            padding-left: 4px;
        }
    }
}


.btnExit {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    flex-grow: 1;
    .btn {
        padding: 10px 20px;
        border: none;
        border-radius: 5px;
        margin-right: 10px;
        text-align: center;
        width: 200px;
    }
    .cancel-changes {
        font-family: $text-medium;
        color: $main-s10;
        background-color: $neutro;
        margin: 6px;
    }
    .save-changes {
        font-family: $text-bold;
        color: #fff;
        background-color: $main;
    }
}
</style>
